import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminAlertText,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminPageTitle,
  AdminPanelTitle,
  AdminSupportingText,
  adminFormDetailSelectClasses,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import {
  ACCOUNT_TYPE_OPTIONS,
  APPEAL_AVAILABILITY_OPTIONS,
  APPEALS_CONFIG_ROUTE,
  PLATFORM_APPEAL_LIMIT,
  PLATFORM_APPEAL_OUTCOMES_DISPLAY,
  PLATFORM_APPEAL_WINDOW,
  PLATFORM_APPEAL_WINDOW_DEADLINE,
  PLATFORM_APPEAL_WINDOW_STARTS,
  PLATFORM_APPLIED_SETTINGS_NOTE,
  PLATFORM_DEPENDENCIES,
  PLATFORM_ELIGIBLE_DECISION,
  PLATFORM_REJECTED_BEHAVIOUR,
  PLATFORM_WITHDRAWN_NOTE,
  SOURCE_MODULE_OPTIONS,
  applyAppealSelectionChange,
  buildSampleAppealRules,
  createEmptyAppealForm,
  formatAppealsDate,
  formatAppealsDateTime,
  getAppealAvailabilityLabel,
  getApprovedActionLabel,
  getApprovedActionOptions,
  getAccountTypeDisplay,
  getCorrectiveActionMeaning,
  getDecisionDefinition,
  getDecisionTypeLabel,
  getDecisionTypeOptionsForModule,
  getDependencyAvailability,
  getGeneratedAccessDetail,
  getGeneratedAccessSummary,
  getSourceModuleLabel,
  nextAppealRuleId,
  normalizeAccountTypes,
  normalizeAppealForm,
} from '../../../data/appealsConfig';
import {
  collectAppealSaveErrors,
  getEditRuleSource,
  getPublishedRule,
  mergeRulesAfterActivate,
} from '../../../data/appealsRuleWorkflow';
import ConfigurationRuleTestResultPanel from './shared/ConfigurationRuleTestResultPanel';

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">
        {value || '—'}
      </AdminDetailFieldValue>
    )}
  </div>
);

function emptyForm() {
  return {
    ...createEmptyAppealForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeAppealForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: getLabel(value) || `${value} Unavailable` }];
};

const AppealsDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { configId, ruleName } = useParams();
  const routeId = configId || ruleName;
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const ruleFromState = location.state?.rule ?? location.state?.configuration;
  const existingRulesFromState = location.state?.existingRules ?? location.state?.configurations;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleAppealRules(),
    [existingRulesFromState]
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (routeId) {
      const found = allRules.find(
        (rule) => String(rule.id).toLowerCase() === String(routeId).toLowerCase()
      );
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, routeId, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit
        ? getEditRuleSource(foundRecord)
        : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && routeId) {
      const found = allRules.find(
        (rule) => String(rule.id).toLowerCase() === String(routeId).toLowerCase()
      );
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, routeId, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});
  const [testResultFeedback, setTestResultFeedback] = useState(null);

  const sourceOptions = useMemo(
    () => withCurrentOption(SOURCE_MODULE_OPTIONS, formData.sourceModule, getSourceModuleLabel),
    [formData.sourceModule]
  );
  const decisionOptions = useMemo(
    () =>
      withCurrentOption(
        getDecisionTypeOptionsForModule(formData.sourceModule),
        formData.decisionType,
        (value) => getDecisionTypeLabel(formData.sourceModule, value)
      ),
    [formData.sourceModule, formData.decisionType]
  );
  const actionOptions = useMemo(
    () =>
      withCurrentOption(
        getApprovedActionOptions(
          formData.sourceModule,
          formData.decisionType,
          formData.accountTypes
        ),
        formData.approvedCorrectiveAction,
        (value) =>
          getApprovedActionLabel(formData.sourceModule, formData.decisionType, value)
      ),
    [
      formData.sourceModule,
      formData.decisionType,
      formData.accountTypes,
      formData.approvedCorrectiveAction,
    ]
  );
  const availabilityOptions = useMemo(
    () =>
      withCurrentOption(
        APPEAL_AVAILABILITY_OPTIONS,
        formData.appealAvailability,
        getAppealAvailabilityLabel
      ),
    [formData.appealAvailability]
  );

  const decisionDef = getDecisionDefinition(formData.sourceModule, formData.decisionType);
  const compatibleAccountTypes = decisionDef?.accountTypes || ['customer', 'business'];
  const scopeReady = Boolean(
    formData.sourceModule &&
      formData.decisionType &&
      normalizeAccountTypes(formData.accountTypes).length
  );

  useEffect(() => {
    if (isView) return;
    setFormData(resolvedSource);
  }, [resolvedSource, isView]);

  useEffect(() => {
    const testResult = location.state?.testResult;
    if (!testResult) return;
    setTestResultFeedback(testResult);
    const { testResult: _removed, ...rest } = location.state || {};
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.state, location.pathname, navigate]);

  const handleCancel = () => {
    navigate(APPEALS_CONFIG_ROUTE, {
      state: { rules: allRules, configurations: allRules },
    });
  };

  const handleChange = (field, value) => {
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
      duplicate: undefined,
      overlap: undefined,
      concurrent: undefined,
      dependencies: undefined,
      conditions: undefined,
      activation: undefined,
    }));
    setFormData((prev) => applyAppealSelectionChange(prev, field, value));
  };

  const validateFull = () => {
    const validation = collectAppealSaveErrors(formData, allRules, formData.id || undefined, {
      originalLastUpdated: resolvedSource.lastUpdated,
      originalId: resolvedSource.id,
    });
    setErrors(validation.errors);
    return validation.summary.length === 0;
  };

  const buildRecord = (nowIso, userLabel) => {
    const derived = normalizeAppealForm(formData);
    const id = formData.id || nextAppealRuleId(allRules);
    const isNew = !formData.id;
    return {
      ...derived,
      id,
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: formData.pendingDraft ?? null,
      configurationHistory: [
        ...(formData.configurationHistory || []),
        {
          action: isNew ? 'Add' : 'Edit',
          field: 'Rule',
          previousValue: isNew ? '—' : publishedBaseline?.id || '—',
          newValue: id,
          status: 'saved',
          actor: userLabel,
          timestamp: nowIso,
        },
      ],
      testHistory: formData.testHistory || [],
      dateAdded: isNew ? nowIso : formData.dateAdded || nowIso,
      addedBy: isNew ? userLabel : formData.addedBy || userLabel,
      lastUpdated: nowIso,
      lastUpdatedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    if (!validateFull()) return;
    const record = buildRecord(nowIso, userLabel);
    const merged = mergeRulesAfterActivate(allRules, record, publishedBaseline || {});
    navigate(APPEALS_CONFIG_ROUTE, { state: { rules: merged, configurations: merged } });
  };

  const renderSelect = (
    name,
    label,
    options,
    placeholder,
    disabledExtra = false,
    required = true,
    emptyMessage = 'No compatible choices available.'
  ) => {
    const emptyCompatible = !disabledExtra && !options.length;
    const emptyRequired = emptyCompatible && required;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`apc-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyRequired}
            className={adminFormDetailSelectClasses(
              disabledExtra || emptyRequired,
              Boolean(errors[name])
            )}
          >
            <option value="">{emptyRequired ? emptyMessage : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  if (isView) {
    if (!formData.id && !publishedBaseline) {
      return (
        <AdminLayout>
          <div className="min-w-0">
            <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Appeal Detail</AdminPageTitle>
            <AdminSupportingText className="text-mediumgray">
              Appeal rule not found.
            </AdminSupportingText>
          </div>
        </AdminLayout>
      );
    }

    const deps = getDependencyAvailability(formData.platformDependencies || PLATFORM_DEPENDENCIES);
    const actionLabel = getApprovedActionLabel(
      formData.sourceModule,
      formData.decisionType,
      formData.approvedCorrectiveAction
    );

    return (
      <AdminLayout>
        <div className="min-w-0">
          <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Appeal Detail</AdminPageTitle>

          <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <div className={adminFormTwoColGridClass}>
                <TextValue label="ID" value={formData.id} mono />
                <TextValue
                  label="Source Module"
                  value={getSourceModuleLabel(formData.sourceModule)}
                />
                <TextValue
                  label="Decision Type"
                  value={getDecisionTypeLabel(formData.sourceModule, formData.decisionType)}
                />
                <TextValue
                  label="Account Type"
                  value={getAccountTypeDisplay(formData.accountTypes)}
                />
                <TextValue label="Approved Corrective Action" value={actionLabel} />
                <TextValue
                  label="Appeal Availability"
                  value={getAppealAvailabilityLabel(formData.appealAvailability)}
                />
                <TextValue
                  label="Generated Access"
                  value={getGeneratedAccessDetail(formData.accountTypes)}
                />
                <TextValue label="Optional Conditions" value="No Conditions" />
                <TextValue label="Eligible Decision" value={PLATFORM_ELIGIBLE_DECISION} />
                <TextValue label="Appeal Window" value={PLATFORM_APPEAL_WINDOW} />
                <TextValue label="Starts" value={PLATFORM_APPEAL_WINDOW_STARTS} />
                <TextValue label="Deadline" value={PLATFORM_APPEAL_WINDOW_DEADLINE} />
                <TextValue label="Appeal Limit" value={PLATFORM_APPEAL_LIMIT} />
                <TextValue label="Appeal Outcomes" value={PLATFORM_APPEAL_OUTCOMES_DISPLAY} />
                <TextValue label="Rejected Behaviour" value={PLATFORM_REJECTED_BEHAVIOUR} />
                <TextValue label="Withdrawal" value={PLATFORM_WITHDRAWN_NOTE} />
                <TextValue
                  label="Corrective Action Meaning"
                  value={getCorrectiveActionMeaning(formData.approvedCorrectiveAction)}
                />
                <TextValue label="Applied Settings" value={PLATFORM_APPLIED_SETTINGS_NOTE} />
                <TextValue
                  label="Appeal Submission Form"
                  value={deps.appealSubmissionForm ? 'Available' : 'Unavailable'}
                />
                <TextValue
                  label="Appeal Reply Form"
                  value={deps.appealReplyForm ? 'Available' : 'Unavailable'}
                />
                <TextValue
                  label="Appeal Decision Form"
                  value={deps.appealDecisionForm ? 'Available' : 'Unavailable'}
                />
                <TextValue
                  label="Form Validation"
                  value={deps.formInventoryValidation ? 'Valid' : 'Invalid'}
                />
                <TextValue
                  label="Shared Message Editor"
                  value={deps.sharedMessageEditor ? 'Available' : 'Unavailable'}
                />
                <TextValue label="Date Added" value={formatAppealsDate(formData.dateAdded)} />
                <TextValue label="Added By" value={formData.addedBy} />
                <TextValue
                  label="Last Updated"
                  value={formatAppealsDateTime(formData.lastUpdated)}
                />
                <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
              </div>
            </DashboardMainContainer>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const pageTitle = isCreate
    ? 'Add Appeal Rule'
    : `${formData.id || routeId || 'APC'} — Appeal Rule`;

  const incompatibleAccountSelected = normalizeAccountTypes(formData.accountTypes).some(
    (type) => formData.decisionType && !compatibleAccountTypes.includes(type)
  );

  const validationMessages = Object.values(errors).filter(Boolean);

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={pageTitle}
        onSubmit={handleSave}
        showSave={false}
        afterForm={<ConfigurationRuleTestResultPanel testResult={testResultFeedback} />}
      >
        {/* Section 1 — Rule Details */}
        <div className="space-y-4 w-full min-w-0">
          {!isCreate ? (
            <AdminPanelTitle className="text-offblack !mb-0">Rule Details</AdminPanelTitle>
          ) : null}
          {!isCreate ? (
            <AdminConfigLockedField
              label="Rule ID"
              value={formData.id || 'Assigned after Save'}
              mono
              isView={false}
            />
          ) : null}
          {renderSelect(
            'approvedCorrectiveAction',
            'Approved Corrective Action',
            actionOptions,
            scopeReady ? 'Select approved corrective action' : 'Complete scope selections first',
            !scopeReady
          )}
          {renderSelect(
            'appealAvailability',
            'Appeal Availability',
            availabilityOptions,
            'Select Enabled or Disabled'
          )}
          <AdminConfigLockedField
            label="Generated Access"
            value={getGeneratedAccessSummary(formData.accountTypes)}
            isView={false}
          />
          {!isCreate ? (
            <>
              <AdminSupportingText className="text-mediumgray mb-0">
                {PLATFORM_ELIGIBLE_DECISION}
              </AdminSupportingText>
              <AdminSupportingText className="text-mediumgray mb-0">
                Appeal Window: {PLATFORM_APPEAL_WINDOW}. {PLATFORM_APPEAL_LIMIT}
              </AdminSupportingText>
              <AdminSupportingText className="text-mediumgray mb-0">
                Outcomes: {PLATFORM_APPEAL_OUTCOMES_DISPLAY}. Rejected: {PLATFORM_REJECTED_BEHAVIOUR}
              </AdminSupportingText>
              <AdminSupportingText className="text-mediumgray mb-0">
                Withdrawal: {PLATFORM_WITHDRAWN_NOTE}
              </AdminSupportingText>
              {formData.approvedCorrectiveAction ? (
                <AdminSupportingText className="text-mediumgray mb-0">
                  Action meaning: {getCorrectiveActionMeaning(formData.approvedCorrectiveAction)}
                </AdminSupportingText>
              ) : null}
              <AdminSupportingText className="text-mediumgray mb-0">
                {PLATFORM_APPLIED_SETTINGS_NOTE}
              </AdminSupportingText>
            </>
          ) : null}
        </div>

        {/* Section 2 — Rule Scope */}
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Scope</AdminPanelTitle>
          {renderSelect('sourceModule', 'Source Module', sourceOptions, 'Select source module')}
          {renderSelect(
            'decisionType',
            'Decision Type',
            decisionOptions,
            formData.sourceModule ? 'Select decision type' : 'Select source module first',
            !formData.sourceModule
          )}

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Account Type <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="space-y-2 border border-lightgray rounded-xl px-3 py-3">
              {ACCOUNT_TYPE_OPTIONS.map((option) => {
                const compatible =
                  !formData.decisionType || compatibleAccountTypes.includes(option.value);
                const checked = normalizeAccountTypes(formData.accountTypes).includes(
                  option.value
                );
                return (
                  <label
                    key={option.value}
                    className={`flex items-start gap-2 text-sm ${
                      compatible ? 'text-offblack' : 'text-mediumgray'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="accent-primaryblue mt-0.5 shrink-0"
                      checked={checked}
                      disabled={!formData.decisionType}
                      onChange={(e) => {
                        const next = new Set(normalizeAccountTypes(formData.accountTypes));
                        if (e.target.checked) next.add(option.value);
                        else next.delete(option.value);
                        handleChange('accountTypes', [...next]);
                      }}
                    />
                    <span className="break-words">
                      {option.label}
                      {!compatible && formData.decisionType
                        ? ' (incompatible with Decision Type)'
                        : ''}
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.accountTypes ? (
              <AdminFormFieldError>{errors.accountTypes}</AdminFormFieldError>
            ) : null}
            {incompatibleAccountSelected ? (
              <AdminFormFieldError>
                This Decision Type does not support the selected Account Type.
              </AdminFormFieldError>
            ) : null}
          </div>
        </div>

        {/* Section 3 — Optional Conditions */}
        <div className="space-y-2 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Optional Conditions</AdminPanelTitle>
          <AdminSupportingText className="text-mediumgray mb-0">
            No Conditions supported.
          </AdminSupportingText>
          {errors.conditions ? (
            <AdminFormFieldError>{errors.conditions}</AdminFormFieldError>
          ) : null}
        </div>

        {/* Section 4 — Validation Messages */}
        <div className="space-y-2 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
          {validationMessages.length > 0 ? (
            validationMessages.map((msg) => (
              <AdminAlertText key={msg} className="mb-0">
                {msg}
              </AdminAlertText>
            ))
          ) : (
            <AdminSupportingText className="text-mediumgray mb-0">
              No validation messages.
            </AdminSupportingText>
          )}
        </div>

        {/* Section 5 — Cancel and Save */}
        <div className="flex items-center justify-end gap-3">
          <AdminFormSecondaryAction type="button" onClick={handleCancel}>
            Cancel
          </AdminFormSecondaryAction>
          <AdminHeaderAction type="submit">Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default AppealsDetail;
